import { createRoute, lazyRouteComponent, useNavigate } from "@tanstack/react-router";
import { captureEvent } from "../utils/o11y";
import { datadogRum } from "@datadog/browser-rum";
import { z } from "zod";
import { lazy, Suspense, useEffect } from "react";

import { Layout } from "./layout/Skeleton";
import { isMobile } from "../shared/hooks";
import { rootRoute } from "../baseRoutes";
import { isDev } from "../globals";
import { useFeatureFlags } from "../context/flags";
import { MultiPaneShell } from "./MultiPaneView";
import Loading from "./components/Loading";
import { hideInitialLoader, showInitialLoader } from "./components/initial-loader";

const EVENT_LIVE_CHARTS_VIEWED = "live_charts_viewed";

const rootWebappRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "app",
  component: Layout,
  onEnter: () => {
    document.title = "Artis";
  },
});

const marketSearchSchema = z.object({
  columnSettingsModal: z
    .object({
      columnId: z.string().optional(),
      initialColumnWidth: z.number().optional(),
      initialThousandsSeparator: z.boolean().optional(),
      initialDecimalPlaces: z.number().optional(),
      increment: z.number().optional(),
    })
    .optional(),
});

const userGuideSchema = z.object({
  id: z.string().optional(),
});

const componentsRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "components",
  component: isDev ? lazyRouteComponent(() => import("./ui/components"), "Components") : undefined,
});

const MarketHome = lazy(() => import("./MarketView").then((m) => ({ default: m.MarketHome })));
const MarketPage = lazy(() => import("./MarketView").then((m) => ({ default: m.MarketPage })));

function MarketHomeGate() {
  const [multiPane, scratchpad] = useFeatureFlags("multi-pane-layout", "scratchpad");
  if (multiPane || scratchpad) return <MultiPaneShell />;
  return (
    <Suspense fallback={<Loading showLogo syncing={false} />}>
      <MarketHome />
    </Suspense>
  );
}

function MarketPageGate() {
  const [multiPane, scratchpad] = useFeatureFlags("multi-pane-layout", "scratchpad");
  const multiPaneActive = multiPane || scratchpad;
  const nav = useNavigate();
  useEffect(() => {
    if (multiPaneActive) nav({ to: "/app/market", replace: true });
  }, [multiPaneActive, nav]);
  if (multiPaneActive) return <Loading showLogo syncing={false} />;
  return (
    <Suspense fallback={<Loading showLogo syncing={false} />}>
      <MarketPage />
    </Suspense>
  );
}

const marketHomeRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "market",
  validateSearch: marketSearchSchema,
  onEnter: () => datadogRum.startView({ name: "Market Home" }),
  component: MarketHomeGate,
});

const marketPageRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "market/$id",
  validateSearch: marketSearchSchema,
  onEnter: () => datadogRum.startView({ name: "Market Page" }),
  component: MarketPageGate,
});

const managePagesRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "manage-pages",
  onEnter: () => datadogRum.startView({ name: "Manage Pages" }),
  component: () => <div> manage pages </div>,
});

const curveManagementSearchSchema = z.object({
  mode: z.enum(["create", "edit"]).optional(),
  edit: z.string().optional(),
  copy: z.string().optional(),
});

function FullScreenRoutePending() {
  useEffect(() => {
    showInitialLoader();
    return () => {
      hideInitialLoader();
    };
  }, []);

  // Use the exact existing global loader from index.html to keep visuals
  // pixel-identical to the rest of the webapp loading experience.
  return <div className="w-full h-dvh" />;
}

const curveManagementRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "curve-management",
  validateSearch: curveManagementSearchSchema,
  pendingMs: 0,
  pendingComponent: FullScreenRoutePending,
  onEnter: () => {
    document.title = "Curve Management";
    datadogRum.startView({ name: "Curve Management" });
  },
  onLeave: () => {
    document.title = "Artis";
  },
  component: lazyRouteComponent(() => import("./curve-management/CurveManagement"), "CurveManagement"),
});

const userGuideRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "user-guide",
  validateSearch: userGuideSchema,
  onEnter: () => datadogRum.startView({ name: "User Guide" }),
  component: lazyRouteComponent(() => import("./UserGuide"), "UserGuide"),
});

const catalogueRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "catalogue",
  pendingMs: 0,
  pendingComponent: FullScreenRoutePending,
  onEnter: () => {
    document.title = "Product Catalogue";
    datadogRum.startView({ name: "Product Catalogue" });
  },
  onLeave: () => {
    document.title = "Artis";
  },
  component: lazyRouteComponent(() => import("./catalogue/CataloguePage"), "CataloguePage"),
});

const tradingChartsRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "trading-chart",
  onEnter: () => {
    document.title = "Artis Charts";
    datadogRum.startView({ name: "Trading Chart" });
    captureEvent(EVENT_LIVE_CHARTS_VIEWED, { mobile: isMobile });
  },
  onLeave: () => {
    document.title = "Artis";
  },
  component: lazyRouteComponent(() => import("./live-charts"), "ChartsPage"),
});

const tradingChartRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "trading-chart/page/$pageId",
  onEnter: () => {
    document.title = "Artis Charts";
    datadogRum.startView({ name: "Trading Chart" });
    captureEvent(EVENT_LIVE_CHARTS_VIEWED, { mobile: isMobile });
  },
  onLeave: () => {
    document.title = "Artis";
  },
  component: lazyRouteComponent(() => import("./live-charts"), "ChartPage"),
});

const singleTradingChartRoute = createRoute({
  getParentRoute: () => rootWebappRoute,
  path: "trading-chart/$productId/$month",
  onEnter: (p) => {
    document.title = "Artis Charts";
    datadogRum.startView({ name: "Trading Chart Single" });
    captureEvent("live_charts_single_viewed", {
      ...p.params,
      mobile: isMobile,
    });
  },
  onLeave: () => {
    document.title = "Artis";
  },
  validateSearch: z.object({
    productId: z.string().optional(),
    month: z.string().optional(),
    instance: z.string().optional(),
  }),
  component: lazyRouteComponent(() => import("./live-charts"), "SingleChartPage"),
});

export const webappRoute = rootWebappRoute.addChildren([
  componentsRoute,
  marketHomeRoute,
  marketPageRoute,
  managePagesRoute,
  curveManagementRoute,
  catalogueRoute,
  tradingChartsRoute,
  tradingChartRoute,
  singleTradingChartRoute,
  userGuideRoute,
]);
